import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import Button from '@components/button/Button';
import CalendarInput from '@components/calendar-input/CalendarInput';
import Dropdown from '@components/drop-down/Dropdown';
import Input from '@components/input/Input';
import TextArea from '@components/text-area/TextArea';
import TimePicker from '@components/time-picker/TimePicker';
import { LOCAL_STORAGE_KEYS } from '@constants/storage';
import { Event, sendMixpanelEvent } from '@core/analytics/mixpanel';
import {
  INPUT_BASE_CLASSNAME,
  INPUT_FOCUS_CLASSNAME
} from '@features/lead-management/constants/common';
import {
  getTaskStatus,
  getTaskStatusOptions,
  isTaskOverdue
} from '@features/lead-management/utils/leadActivity';
import useNotify from '@hooks/notify';
import {
  appendTimeToISODate,
  formatISODate,
  getCurrentISODateString,
  getCurrentTimeString
} from '@utils/date';
import { getLocalStorageItem, setLocalStorageItem } from '@utils/storage';

import {
  TaskStatus,
  TaskStatusEnum,
  TaskTypeOptions
} from '../../constants/leadActivity';
import {
  useCreateLeadTaskMutation,
  useGetGoogleAuthStatusQuery,
  useLazyGetGoogleAuthUrlQuery
} from '../../queries/LeadManagementQuery';
import {
  TaskFormData,
  TaskModalProps,
  TaskStatusValue,
  TaskTypeValue
} from '../../types/leadActivity';

const AddTaskModal: React.FC<TaskModalProps> = ({
  onClose,
  onSubmit,
  initialData = {},
  leadId,
  analyticsData
}) => {
  const { leadDetails } = analyticsData ?? {};
  const organisationId = getLocalStorageItem(
    LOCAL_STORAGE_KEYS.ORGANIZATION_ID
  );
  const { currentOrg } = useSelector((state: any) => state.user);
  const isEditing = Boolean(initialData.id);

  const { createErrorAlert, createSuccessAlert } = useNotify();

  const [createLeadTask, { isLoading }] = useCreateLeadTaskMutation();

  const { data: googleAuthData, isLoading: isLoadingGoogleAuth } =
    useGetGoogleAuthStatusQuery(
      { organizationId: organisationId },
      { skip: !organisationId }
    );

  const [getGoogleAuthUrl, { isLoading: isLoadingAuthUrl }] =
    useLazyGetGoogleAuthUrlQuery();

  const isConnected = googleAuthData?.is_authenticated || false;
  const initialDate = getCurrentISODateString();
  const initialTime = getCurrentTimeString();
  const [formData, setFormData] = useState<TaskFormData>({
    name: '',
    date: initialDate,
    time: initialTime,
    remarks: '',
    taskType: TaskTypeOptions[0].id as TaskTypeValue,

    // derive the status from the pre-populated date/time
    taskStatus: getTaskStatus(
      initialDate,
      initialTime,
      TaskStatus[0].id
    ) as TaskStatusValue
  });
  const [error, setError] = useState<{ name: string }>({ name: '' });

  const handleInputChange = useCallback(
    (field: keyof TaskFormData, value: any) => {
      if (field === 'name') {
        setError({ ...error, name: '' });
      }
      setFormData((prev) => ({
        ...prev,
        [field]: value
      }));
    },
    [error]
  );

  const handleDateAndtimeChange = useCallback(
    <K extends keyof TaskFormData>(key: K, value: TaskFormData[K]) => {
      setFormData((prev) => {
        const newFormData = { ...prev, [key]: value } as TaskFormData;
        const isOverdue = isTaskOverdue(newFormData.date, newFormData.time);
        // if overdue handle completed and overdue status selection
        if (isOverdue) {
          return {
            ...newFormData,
            taskStatus:
              newFormData.taskStatus === TaskStatusEnum.COMPLETED
                ? (TaskStatusEnum.COMPLETED as TaskStatusValue)
                : (TaskStatusEnum.OVERDUE as TaskStatusValue)
          };
        }
        // if not overdue set scheduled status
        return {
          ...newFormData,
          taskStatus: TaskStatusEnum.SCHEDULED as TaskStatusValue
        };
      });
    },
    []
  );

  const isFormValid = useCallback(
    () =>
      !!(
        formData.name &&
        formData.date &&
        formData.time &&
        formData.taskType &&
        formData.taskStatus &&
        !error.name
      ),
    [formData, error]
  );

  const handleSubmit = useCallback(async () => {
    try {
      const isOverDue =
        formData.taskStatus === (TaskStatusEnum.OVERDUE as TaskStatusValue);
      const timestamp = appendTimeToISODate(formData.date, formData.time);

      const submitData = {
        id: formData.id,
        name: formData.name,
        task_type: formData.taskType as TaskTypeValue,
        // handle completed status selection
        status: isOverDue
          ? (TaskStatusEnum.SCHEDULED as TaskStatusValue)
          : (formData.taskStatus as TaskStatusValue),
        timestamp,
        remarks: formData.remarks ? formData.remarks : null
      };

      const response = await createLeadTask({
        leadId,
        organizationId: organisationId,
        task: submitData
      }).unwrap();

      if (!('errors' in response)) {
        // Mixpanel event for task added/updated
        const taskStatusForEvent = getTaskStatus(
          formData.date,
          formData.time,
          formData.taskStatus
        );

        sendMixpanelEvent(
          isEditing
            ? Event.LEAD_ACTIVITIES_TASK_UPDATED
            : Event.LEAD_ACTIVITIES_TASK_ADDED,
          {
            org_id: organisationId,
            org_name: currentOrg?.name,
            lead_id: leadId,
            lead_name: leadDetails?.leadName,
            lead_phone: leadDetails?.leadPhone,
            task_id: response?.id || formData.id,
            scheduled_datetime: timestamp,
            task_type: formData.taskType,
            task_status: taskStatusForEvent
          }
        );
        createSuccessAlert(
          `Task ${isEditing ? 'edited' : 'created'} successfully`
        );
        onSubmit();
      } else {
        createErrorAlert(`Failed to ${isEditing ? 'update' : 'create'} task`);
        onClose();
      }
    } catch (submitError) {
      createErrorAlert(`Failed to ${isEditing ? 'update' : 'create'} task`);
      onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formData,
    createLeadTask,
    onClose,
    leadId,
    leadDetails?.leadName,
    leadDetails?.leadPhone,
    createErrorAlert,
    createSuccessAlert,
    organisationId,
    onSubmit,
    isEditing
  ]);

  const handleSyncGoogleCalendar = useCallback(async () => {
    if (isConnected) {
      createSuccessAlert('Google Calendar is already connected!');
    } else {
      try {
        const activityDetails = {
          type: 'task',
          data: {
            id: formData.id,
            name: formData.name,
            date: formData.date,
            time: formData.time,
            remarks: formData.remarks,
            taskType: formData.taskType,
            taskStatus: formData.taskStatus,
            leadId,
            organizationId: organisationId
          }
        };

        setLocalStorageItem(
          LOCAL_STORAGE_KEYS.ACTIVITY_DETAILS,
          activityDetails
        );

        const redirectUrl = encodeURIComponent(
          `${window.location.href}?success=true`
        );
        const result = await getGoogleAuthUrl({
          organizationId: organisationId,
          redirectUrl
        }).unwrap();

        const authUrl = result?.auth_url;

        if (authUrl) {
          window.location.href = authUrl;
        } else {
          throw new Error('No auth URL received from server');
        }
      } catch (e) {
        createErrorAlert(
          'Failed to connect to Google Calendar. Please try again.'
        );
      }
    }
  }, [
    isConnected,
    getGoogleAuthUrl,
    organisationId,
    createSuccessAlert,
    createErrorAlert,
    formData,
    leadId
  ]);

  const handleClose = () => {
    onClose();
  };

  const handleInputBlur = (value: string) => {
    if (value.length < 3 || value.length > 30) {
      setError({
        ...error,
        name: 'Task name must be between 3 to 30 characters'
      });
    } else {
      setError({ ...error, name: '' });
    }
  };

  useEffect(() => {
    if (isEditing) {
      setFormData({
        ...initialData,
        taskStatus:
          isTaskOverdue(initialData.date || '', initialData.time || '') &&
          initialData.taskStatus !== TaskStatusEnum.COMPLETED
            ? (TaskStatusEnum.OVERDUE as TaskStatusValue)
            : (initialData.taskStatus as TaskStatusValue)
      } as TaskFormData);
    }
  }, [initialData, isEditing]);

  useEffect(() => {
    const activityDetails = getLocalStorageItem(
      LOCAL_STORAGE_KEYS.ACTIVITY_DETAILS
    );
    if (activityDetails?.type === 'task') {
      setFormData(activityDetails.data);
    }
  }, []);

  return (
    <>
      <div className="px-6 pb-1">
        <div className="space-y-1.5">
          <label className="text-sm font-medium text-gray-700">
            Task Name <span className="text-red-500">*</span>
          </label>
          <Input
            value={formData.name}
            handleInputChange={(value: string) =>
              handleInputChange('name', value)
            }
            placeholder="Name of the task"
            className="w-full"
            inputStyle={INPUT_BASE_CLASSNAME}
            handleInputBlur={handleInputBlur}
          />
          {error.name && (
            <p className="mt-1 text-sm text-red-500">{error.name}</p>
          )}
        </div>

        <div className="mt-2 grid grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-gray-700">
              Date <span className="text-red-500">*</span>
            </label>
            <div className="flex h-12.5 items-center">
              <CalendarInput
                selectedDate={
                  formData.date ? new Date(formData.date) : undefined
                }
                handleDateChange={(date?: Date) =>
                  handleDateAndtimeChange(
                    'date',
                    date
                      ? formatISODate({
                          date,
                          separator: '-',
                          format: 'yyyy-mm-dd'
                        })
                      : ''
                  )
                }
                placeholder="Select Date"
                className="w-full [&_input]:!flex [&_input]:!h-12.5 [&_input]:!items-center"
                isMaxDateRequired={false}
                inputStyle={INPUT_BASE_CLASSNAME}
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-gray-700">
              Time <span className="text-red-500">*</span>
            </label>
            <TimePicker
              label=""
              time={formData.time}
              handleChange={(value: string) =>
                handleDateAndtimeChange('time', value)
              }
              defaultToCurrentTime
              labelStyle="hidden"
              className="w-full"
              popupClassName="!fixed !top-1/2 !left-1/2 !transform !-translate-x-1/2 !-translate-y-1/2 !z-[9999]"
              pickerInputStyles={`${INPUT_BASE_CLASSNAME} ${INPUT_FOCUS_CLASSNAME}`}
            />
          </div>
        </div>

        <div className="mt-2 grid grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-gray-700">
              Task Type <span className="text-red-500">*</span>
            </label>
            <div className="flex h-12.5 items-center">
              <Dropdown
                filterOptions={TaskTypeOptions}
                currentFilter={formData.taskType}
                onSelectFilter={(value: string) =>
                  handleInputChange('taskType', value)
                }
                placeholder="Select type of task"
                className={`w-full ${INPUT_FOCUS_CLASSNAME}`}
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-gray-700">
              Task Status <span className="text-red-500">*</span>
            </label>
            <div className="flex h-12.5 items-center">
              <Dropdown
                filterOptions={getTaskStatusOptions(
                  formData.date,
                  formData.time
                )}
                currentFilter={formData.taskStatus}
                onSelectFilter={(value: string) =>
                  handleInputChange('taskStatus', value)
                }
                placeholder="Select status of task"
                className={`w-full ${INPUT_FOCUS_CLASSNAME}`}
              />
            </div>
          </div>
        </div>
        <div className="mt-2 space-y-1.5">
          <label className="text-sm font-medium text-gray-700">Remarks</label>
          <TextArea
            value={formData.remarks}
            onChange={(value: string) => handleInputChange('remarks', value)}
            placeholder="Add a note"
            rows={3}
            className="w-full"
            inputStyle={INPUT_BASE_CLASSNAME}
          />
        </div>
        {!isConnected && !isLoadingGoogleAuth && (
          <Button
            className="cursor-pointer space-y-0 !p-0 text-base font-medium text-[#1C80D5]"
            handleClick={handleSyncGoogleCalendar}
            variant="transparent"
          >
            Sync Google Calendar
          </Button>
        )}
      </div>

      <div className="flex gap-4 rounded-b-xl border-t border-gray-200 p-6 pt-2">
        <Button
          variant="secondary"
          handleClick={handleClose}
          label="Cancel"
          className="flex-1"
        />
        <Button
          handleClick={handleSubmit}
          label="Done"
          className="flex-1"
          loading={isLoading || isLoadingAuthUrl}
          disabled={!isFormValid()}
        />
      </div>
    </>
  );
};

export default AddTaskModal;
